import { ClippingStudioAudioEvent } from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { ClippingStudioMainAudioTrack } from "app/js/src/domain/clipping-studio/clipping-studio-main-audio-track";

export { ClippingStudioMainAudioTrack };

/** Stable event id for the single main-source audio selection row in project.audio. */
export const MAIN_SOURCE_AUDIO_EVENT_ID = "main-source-audio";

export type MainAudioSelection =
  | { kind: "track"; mediaId: number }
  | { kind: "embedded" };

export interface ResolveMainAudioSelectionResult {
  selection: MainAudioSelection;
  /** True when an isMainSource event exists but is invalid for the current track list. */
  shouldStripStaleEvent: boolean;
}

export function findMainSourceAudioEvent(
  audio: ClippingStudioAudioEvent[] | null | undefined,
): ClippingStudioAudioEvent | undefined {
  if (!audio?.length) return undefined;
  return audio.find((e) => e?.isMainSource === true);
}

/**
 * Upserts a single isMainSource event for the given linked audio media id.
 * Removes any other isMainSource rows so at most one remains.
 */
export function upsertMainSourceAudioTrack(
  audio: ClippingStudioAudioEvent[] | null | undefined,
  mediaId: number,
): ClippingStudioAudioEvent[] {
  const withoutMain = clearMainSourceAudioEvent(audio);
  if (mediaId == null || mediaId <= 0) {
    return withoutMain;
  }
  return [
    ...withoutMain,
    {
      id: MAIN_SOURCE_AUDIO_EVENT_ID,
      mediaId,
      inMs: 0,
      outMs: 1,
      isMainSource: true,
    },
  ];
}

/** Removes all isMainSource events (embedded selection = absence of the event). */
export function clearMainSourceAudioEvent(
  audio: ClippingStudioAudioEvent[] | null | undefined,
): ClippingStudioAudioEvent[] {
  if (!audio?.length) return [];
  return audio.filter((e) => e?.isMainSource !== true);
}

/**
 * Resolves main preview selection from persisted audio events + available tracks.
 * No matching isMainSource event => embedded (product default for main).
 */
export function resolveMainAudioSelection(
  audio: ClippingStudioAudioEvent[] | null | undefined,
  audioFiles: ClippingStudioMainAudioTrack[] | null | undefined,
): ResolveMainAudioSelectionResult {
  const event = findMainSourceAudioEvent(audio);
  const tracks = audioFiles ?? [];
  if (event == null) {
    return { selection: { kind: "embedded" }, shouldStripStaleEvent: false };
  }
  const mediaId = Number(event.mediaId);
  if (
    !Number.isFinite(mediaId) ||
    mediaId <= 0 ||
    tracks.length === 0 ||
    !tracks.some((t) => t.id === mediaId)
  ) {
    return { selection: { kind: "embedded" }, shouldStripStaleEvent: true };
  }
  return {
    selection: { kind: "track", mediaId },
    shouldStripStaleEvent: false,
  };
}

/** Full display name for a track (name, else language, else Audio #id). */
export function mainAudioTrackFullLabel(
  track: ClippingStudioMainAudioTrack,
): string {
  const name = track?.name?.trim();
  if (name) return name;
  const lang = track?.language?.trim();
  if (lang) return lang;
  return `Audio #${track.id}`;
}

/** Truncated label for dropdown trigger/options: 8 chars + "..." when longer. */
export function truncateMainAudioLabel(
  full: string,
  maxLen = 8,
): string {
  const s = (full ?? "").trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}...`;
}

export const MAIN_AUDIO_EMBEDDED_LABEL = "default";
